import { designVariables } from "@studio3t/design-variables";
import {
  ButtonMediumPrimary,
  ButtonMediumSecondary,
  RadioButton,
} from "@studio3t/generic-components";
import {
  type ReactNode,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";
import styled from "styled-components";
import { reportsApi } from "@/api/client";
import { DateTimeField } from "@/components/DateTimeField";
import {
  ModalBackdrop,
  ModalCard,
  ModalFooter,
  ModalFooterActions,
  ModalHeader,
  ModalTitle,
} from "@/components/ModalChrome";
import { ModalCloseButton } from "@/components/ModalCloseButton";
import { useToast } from "@/components/toast/ToastProvider";
import { useModalFocus } from "@/hooks/useModalFocus";
import { useModalKeys } from "@/hooks/useModalKeys";
import { saveTextFile } from "@/utils/saveFile";
import {
  FUTURE_MOMENT,
  isFutureMoment,
  type ReportFormat,
  type ReportOptions,
  reportFilename,
  reportMimeType,
  reportProblem,
} from "./processingReport";

const MODAL = designVariables.ui.modal;
const SEMANTIC = designVariables.ui.semantic;
const FORM = designVariables.ui.form;
const INPUT = designVariables.ui.input;
const SINGLE_LINE = designVariables.type.singleLine;
const PARAGRAPH = designVariables.type.paragraph;

// The page's side of the download: whether the modal is open, and the
// fetch-then-save the modal drives — fetched with the bearer header a
// plain download link cannot carry, handed to the browser under the
// report's own name.
export function useReportDownload(pipeline: string) {
  const [open, setOpen] = useState(false);
  const download = useCallback(
    async (options: ReportOptions) => {
      const text = await reportsApi.pipeline(pipeline, options);
      saveTextFile(
        text,
        reportFilename(pipeline, options.format, options.reportedFor),
        reportMimeType(options.format),
      );
    },
    [pipeline],
  );
  return { open, setOpen, download };
}

// Whether a download has been running long enough to show for: the lock
// on the buttons is immediate, but their busy look waits this long, so a
// request the engine answers in a few milliseconds does not flash every
// control grey and back.
const BUSY_AFTER_MS = 300;

function useShownBusy(downloading: boolean): boolean {
  const [shown, setShown] = useState(false);
  useEffect(() => {
    if (!downloading) {
      setShown(false);
      return;
    }
    const timer = setTimeout(() => setShown(true), BUSY_AFTER_MS);
    return () => clearTimeout(timer);
  }, [downloading]);
  return shown;
}

// The toasts this modal has raised and not yet taken back: the warnings
// about a moment ahead of the clock, and the errors of refused downloads.
// A valid moment retracts the warnings; closing the modal retracts all —
// by Cancel, the ×, the backdrop, Escape, a finished download, or the
// page unmounting under it.
function useRaisedToasts(isOpen: boolean) {
  const { toast, dismiss } = useToast();
  const warnings = useRef<number[]>([]);
  const errors = useRef<number[]>([]);

  const warn = useCallback(
    (message: ReactNode) => {
      warnings.current.push(toast({ state: "Warning", message }));
    },
    [toast],
  );
  const fail = useCallback(
    (message: ReactNode) => {
      errors.current.push(toast({ state: "Error", message }));
    },
    [toast],
  );
  const retractWarnings = useCallback(() => {
    for (const id of warnings.current) dismiss(id);
    warnings.current = [];
  }, [dismiss]);
  const retractAll = useCallback(() => {
    retractWarnings();
    for (const id of errors.current) dismiss(id);
    errors.current = [];
  }, [dismiss, retractWarnings]);

  useEffect(() => {
    if (!isOpen) return;
    return retractAll;
  }, [isOpen, retractAll]);

  return { warn, fail, retractWarnings };
}

// The modal's choices and the download they drive: fresh per open, a
// refusal announced on a toast in place of a broken file, the modal left
// open so the date can be changed. The keys (Escape closes, Enter
// downloads) are bound by the component, which knows the date's verdict.
function useReportChoices(options: {
  isOpen: boolean;
  onDownload: (report: ReportOptions) => Promise<void>;
  onClose: () => void;
}) {
  const { isOpen, onDownload, onClose } = options;
  const [format, setFormat] = useState<ReportFormat>("csv");
  const [reportedFor, setReportedFor] = useState<Date | undefined>(undefined);
  const [downloading, setDownloading] = useState(false);
  // The lock a second click checks: a ref, because two clicks in one tick
  // would both read the state as it was rendered, before the first set it.
  const inFlight = useRef(false);
  const busy = useShownBusy(downloading);
  const raised = useRaisedToasts(isOpen);

  useEffect(() => {
    if (!isOpen) return;
    setFormat("csv");
    setReportedFor(undefined);
  }, [isOpen]);

  const close = useCallback(() => {
    if (!downloading) onClose();
  }, [downloading, onClose]);

  // A moment ahead of the clock cannot be reported on: the toast says so
  // as soon as it is picked, and the download holds until it changes. A
  // moment that has happened, or a cleared field, takes the warning back.
  const chooseMoment = useCallback(
    (moment: Date | undefined) => {
      setReportedFor(moment);
      if (isFutureMoment(moment, new Date())) {
        raised.warn(FUTURE_MOMENT);
      } else {
        raised.retractWarnings();
      }
    },
    [raised],
  );

  const download = async () => {
    // The lock is logical from the first click; the look follows later.
    if (inFlight.current) return;
    inFlight.current = true;
    setDownloading(true);
    try {
      await onDownload({
        format,
        reportedFor: reportedFor?.toISOString(),
      });
      onClose();
    } catch (error) {
      raised.fail(reportProblem(error));
    } finally {
      inFlight.current = false;
      setDownloading(false);
    }
  };

  return {
    format,
    setFormat,
    reportedFor,
    chooseMoment,
    /** A download has been running long enough to show for. */
    busy,
    close,
    download,
  };
}

// Download report (TTTL-220): the processing record the engine keeps for
// this pipeline, as CSV, text or JSON, as of now or a chosen moment. The
// page hands the generated document over and does not render it.
export function DownloadReportModal({
  isOpen,
  pipeline,
  onDownload,
  onClose,
}: {
  isOpen: boolean;
  pipeline: string;
  /** Fetches and saves the report; a thrown error is announced on an
   *  error toast, worded by `reportProblem`, with the modal left open. */
  onDownload: (report: ReportOptions) => Promise<void>;
  onClose: () => void;
}) {
  const titleId = useId();
  const groupName = useId();
  const modalRef = useModalFocus(isOpen);
  const c = useReportChoices({ isOpen, onDownload, onClose });
  const future = isFutureMoment(c.reportedFor, new Date());
  // Escape closes; Enter downloads under the same gate as the button.
  useModalKeys({
    active: isOpen,
    onCancel: c.close,
    onConfirm: () => {
      if (!c.busy && !future) void c.download();
    },
  });

  if (!isOpen) return null;

  return (
    <ModalBackdrop onClick={c.close} role="presentation">
      <Modal
        ref={modalRef}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        data-testid="download-report-modal"
      >
        <ModalHeader>
          <ModalTitle id={titleId}>Download report</ModalTitle>
          <ModalCloseButton
            $top={MODAL.header.paddingh}
            $right={MODAL.paddingw}
            onClick={c.close}
            disabled={c.busy}
            aria-label="Close"
          />
        </ModalHeader>
        <Intro>
          A record of the data <PipelineName>{pipeline}</PipelineName>{" "}
          processed: each field, how it is labelled, and what the pipeline did
          with it.
        </Intro>
        <Form>
          {/* The format stays live during a download: the request in
              flight already holds its options, and locking the radios
              only made them blink grey on a fast refusal. */}
          <Field>
            <FieldLabel>Format</FieldLabel>
            <Choices>
              <RadioButton
                groupName={groupName}
                value="csv"
                label="CSV"
                checked={c.format === "csv"}
                onEnable={() => c.setFormat("csv")}
              />
              <RadioButton
                groupName={groupName}
                value="text"
                label="Text"
                checked={c.format === "text"}
                onEnable={() => c.setFormat("text")}
              />
              <RadioButton
                groupName={groupName}
                value="json"
                label="JSON"
                checked={c.format === "json"}
                onEnable={() => c.setFormat("json")}
              />
            </Choices>
          </Field>
          <Field $wide>
            <FieldLabel>Pick a date and time</FieldLabel>
            <InputAndSupportive>
              <DateTimeField
                label="Date and time"
                placeholder="Choose a date to see the record as it was then"
                testId="report-date"
                value={c.reportedFor}
                onChange={c.chooseMoment}
              />
              <Supportive>Leave empty for the current state.</Supportive>
            </InputAndSupportive>
          </Field>
        </Form>
        <Footer>
          <ModalFooterActions>
            <ButtonMediumSecondary
              text="Cancel"
              onClick={c.close}
              disabled={c.busy}
            />
            <ButtonMediumPrimary
              text={c.busy ? "Downloading…" : "Download"}
              onClick={() => void c.download()}
              disabled={c.busy || future}
              data-testid="download-report-confirm"
            />
          </ModalFooterActions>
        </Footer>
      </Modal>
    </ModalBackdrop>
  );
}

// The date field's calendar drops below the field, past the card's
// bottom edge — the card must not clip it, so the footer rounds its own
// corners in place of the card's overflow clipping.
// Figma Modal (demo) 15823-147767.
const Modal = styled(ModalCard)`
  width: 500px;
  overflow: visible;
`;

const Footer = styled(ModalFooter)`
  border-radius: 0 0 ${MODAL.corner}px ${MODAL.corner}px;
`;

// The intro is its own block above the form, a spacingL apart from it.
const Intro = styled.p`
  margin: 0;
  padding: 0 ${MODAL.paddingw}px ${SEMANTIC.spacingl}px;
  font-size: ${PARAGRAPH.default.regular.fontSize}px;
  font-weight: ${PARAGRAPH.default.regular.fontWeight};
  line-height: 1.4;
  color: ${(p) => p.theme.semanticTextBody};
`;

const PipelineName = styled.span`
  font-weight: ${PARAGRAPH.default.medium.fontWeight};
`;

// Form groups a form gap-group apart (Format, the moment).
const Form = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${FORM.gapgroup}px;
  padding: 0 ${MODAL.paddingw}px ${MODAL.paddingb}px;
`;

// Label + control stack; the date group spans the card, Format hugs.
const Field = styled.div<{ $wide?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${INPUT.gaplabel}px;
  ${(p) => p.$wide && "align-self: stretch;"}
`;

// Figma Form/label: Single-line/Default/Medium on a 20px line.
const FieldLabel = styled.span`
  display: flex;
  align-items: center;
  height: 20px;
  font-size: ${SINGLE_LINE.default.medium.fontSize}px;
  font-weight: ${SINGLE_LINE.default.medium.fontWeight};
  line-height: 1.2;
  color: ${(p) => p.theme.inputLabel};
`;

const Choices = styled.div`
  display: flex;
  gap: ${FORM.gapgroup}px;
`;

const InputAndSupportive = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${INPUT.gapsupportive}px;
`;

// Figma Form/supportive: Paragraph/Small/Regular in the label's colour.
const Supportive = styled.span`
  font-size: ${PARAGRAPH.small.regular.fontSize}px;
  font-weight: ${PARAGRAPH.small.regular.fontWeight};
  line-height: 1.4;
  color: ${(p) => p.theme.inputLabel};
`;
